import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { can, ROLES, ROLE_HIERARCHY, UC2, UNIT_COLORS } from "../config";
import { apiCall } from "../api";
import { Input, Select, Btn, TimeField } from "./common/FormElements";
import { Modal } from "./common/Modal";
import { RoleBadge } from "./common/Badge";
import { orderedPermissions } from "../permissions";
import BackupRestore from "./BackupRestore";
import AuditLog from "./AuditLog";
import PMCheckSheetsAdmin from "./PMCheckSheetsAdmin";
import RBACAdmin from "./RBACAdmin";
import MaintenanceTypesAdmin from "./MaintenanceTypesAdmin";
import APIKeysAdmin from "./APIKeysAdmin";
import HRMonthlyExport from "./HRMonthlyExport";
import HRKpiExport from "./HRKpiExport";

// What the reliability report will actually measure this unit on, shown live under the
// shift fields. Mirrors _rel_shift_model() in backend/routers/reports.py: a blank or
// degenerate shift falls back to the plant-wide two-shift day rather than a zero-hour one.
const PLANT_DEFAULT_HOURS = 20;

export function shiftSummary(form) {
  const s = String((form || {}).shiftStart || "").trim();
  const e = String((form || {}).shiftEnd || "").trim();
  const toMin = v => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(v);
    return m ? Number(m[1]) * 60 + Number(m[2]) : -1;
  };
  const a = toMin(s), b = toMin(e);
  if (a < 0 || b < 0 || a === b) {
    return `No shift set — this unit is measured on the plant default of ${PLANT_DEFAULT_HOURS} h a day (two shifts 08:30-19:00 and 19:00-05:30, less the 05:30-08:30 off window and 1 h lunch).`;
  }
  const gross = b > a ? b - a : 24 * 60 - a + b;
  // Lunch is already taken off breakdown time, so it comes off the working day too —
  // derived from the window itself so the two can never disagree. The break field is
  // whatever else the floor stops for, on top of lunch.
  const ls = toMin(String((form || {}).lunchStart || "").trim());
  const le = toMin(String((form || {}).lunchEnd || "").trim());
  let lunch = 0;
  if (ls >= 0 && le > ls) {
    for (const d of [0, 1]) {
      const lo = Math.max(a, ls + d * 1440), hi = Math.min(a + gross, le + d * 1440);
      if (hi > lo) lunch += hi - lo;
    }
  }
  let extra = Number((form || {}).breakMinutes);
  extra = Number.isFinite(extra) && extra > 0 ? extra : 0;
  const brk = Math.min(lunch + extra, gross);
  const hrs = Math.round(((gross - brk) / 60) * 100) / 100;
  return `This unit will be measured on ${s}-${e}${brk ? ` less ${brk} min break${lunch && extra ? " (lunch + extra)" : lunch ? " (lunch)" : ""}` : ""} = ${hrs} h a day`
    + (b <= a ? " (crosses midnight)." : ".")
    + (hrs < PLANT_DEFAULT_HOURS ? ` That is ${Math.round(((PLANT_DEFAULT_HOURS - hrs) / PLANT_DEFAULT_HOURS) * 100)}% fewer running hours than the plant default, so MTBF will fall by about the same share.` : "");
}

export default function Settings({ users, setUsers, units, setUnits, user: me, refreshUsers, refreshUnits, departments, refreshDepartments, floors = [], refreshFloors, refreshMachines, refreshPM, unitId, machines = [] }) {
  const [tab, setTab] = useState("users");
  const [userModal, setUserModal] = useState(null);
  const [unitModal, setUnitModal] = useState(null);
  const [uForm, setUForm] = useState({});
  // Hidden until asked for, and reset every time the dialog opens, so a password is
  // never left on screen for whoever walks past next.
  const [showPwd, setShowPwd] = useState(false);
  const [unForm, setUnForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [deptSaving, setDeptSaving] = useState(false);
  const [newFloorName, setNewFloorName] = useState("");
  const [floorSaving, setFloorSaving] = useState(false);
  const [floorErr, setFloorErr] = useState("");
  const [editFloorId, setEditFloorId] = useState(null);
  const [editFloorName, setEditFloorName] = useState("");
  const [resetConfirm, setResetConfirm] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [deleteUnit, setDeleteUnit] = useState(null);
  const [deletingUnit, setDeletingUnit] = useState(false);
  const [deleteUnitErr, setDeleteUnitErr] = useState("");
  // The Role Permissions summary reads the saved roles rather than a fixed table, so it
  // keeps agreeing with RBAC after a role is edited. Any logged-in user may read /roles.
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesErr, setRolesErr] = useState("");
  // null while reading. Editing holds a draft of roleName -> permission keys, so the
  // ticks on screen are the pending state and nothing is written until Save.
  const [permDraft, setPermDraft] = useState(null);
  const [permSaving, setPermSaving] = useState(false);
  const [permErr, setPermErr] = useState("");

  async function loadRoles() {
    setRolesLoading(true); setRolesErr("");
    try {
      const data = await apiCall("GET", "/roles");
      const list = Array.isArray(data) ? data : [];
      setRoles([...list].sort((a, b) => (b.level || 0) - (a.level || 0)));
    } catch (e) {
      setRolesErr(e?.response?.data?.detail || e?.message || "failed");
    } finally { setRolesLoading(false); }
  }

  function startPermEdit() {
    setPermErr("");
    setPermDraft(Object.fromEntries(roles.map(r => [r.name, [...(r.permissions || [])]])));
  }

  function togglePerm(roleName, key) {
    // Super Admin is all-access by definition and the server ignores permission
    // changes for it, so the column is read-only here rather than offering a tick
    // that silently does nothing.
    if (roleName === "superadmin") return;
    setPermDraft(d => {
      const have = d[roleName] || [];
      return { ...d, [roleName]: have.includes(key) ? have.filter(k => k !== key) : [...have, key] };
    });
  }

  async function savePerms() {
    setPermSaving(true); setPermErr("");
    try {
      // Only the roles that actually changed are written, so saving without edits is
      // a no-op rather than a row of pointless audit entries.
      const changed = roles.filter(r => r.name !== "superadmin" && (
        JSON.stringify([...(r.permissions || [])].sort()) !== JSON.stringify([...(permDraft[r.name] || [])].sort())
      ));
      for (const r of changed) {
        await apiCall("PUT", `/roles/${r.id}`, { permissions: permDraft[r.name] || [] });
      }
      await loadRoles();
      setPermDraft(null);
      if (changed.length) {
        // A permission change only reaches a logged-in user when their token is next
        // checked against the reloaded cache, so say so rather than letting someone
        // think the save failed.
        alert(`Saved ${changed.length} role${changed.length === 1 ? "" : "s"}. Anyone signed in picks up the change on their next page load.`);
      }
    } catch (e) {
      setPermErr(e?.response?.data?.detail || e?.message || "Failed to save");
    } finally { setPermSaving(false); }
  }

  useEffect(() => {
    if (tab !== "users") return;
    loadRoles();
    // Leaving the tab mid-edit drops the draft rather than keeping unsaved ticks
    // waiting to be saved against a list that has since been refetched.
    return () => { setPermDraft(null); setPermErr(""); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Every permission any role holds, so one granted outside the known groups still shows.
  const permissionRows = orderedPermissions(
    [...new Set(roles.flatMap(r => r.permissions || []))]
  );

  const canSeeAll = me.role === "superadmin";
  const visibleUsers = canSeeAll ? users : users.filter(u => u.unitId === me.unitId || u.id === me.id);

  // Users grouped by unit for the side list. "" is a real value here — it means All
  // Units, which is how a Super Admin is stored — so the bucket key has to tell it
  // apart from a unit that no longer exists, hence the explicit sentinels rather than
  // a falsy test.
  const [unitFilter, setUnitFilter] = useState("__all__");
  const userGroups = (() => {
    const counts = new Map();
    for (const u of visibleUsers) {
      const key = u.unitId ? u.unitId : "__none__";
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const rows = [{ key: "__all__", label: "All users", count: visibleUsers.length }];
    if (counts.has("__none__")) {
      rows.push({ key: "__none__", label: "All Units", count: counts.get("__none__"), hint: "Super Admin" });
    }
    for (const un of units) {
      if (counts.has(un.id)) rows.push({ key: un.id, label: un.shortName || un.name || un.id, count: counts.get(un.id) });
    }
    // A user whose unitId matches no unit would otherwise vanish from every bucket and
    // be unreachable from this list — the one case where you most need to find them.
    for (const [key, count] of counts) {
      if (key !== "__none__" && !units.some(un => un.id === key)) {
        rows.push({ key, label: "⚠ Invalid unit", count, hint: key, invalid: true });
      }
    }
    return rows;
  })();
  const filteredUsers = unitFilter === "__all__"
    ? visibleUsers
    : visibleUsers.filter(u => (u.unitId ? u.unitId : "__none__") === unitFilter);

  // Which roles this caller may hand out — the same rule the server enforces, so the
  // list never offers something that fails on save.
  //
  // A Super Admin may appoint another Super Admin: the server already allows it, and
  // without it there could only ever be the one account, with no way to add a second or
  // hand over. Everyone else stays strictly below their own level, so a Unit Admin can
  // make a Supervisor but not another Unit Admin and certainly not a Super Admin.
  const allowedRoles = Object.entries(ROLES).filter(([roleKey]) => (
    me.role === "superadmin" || ROLE_HIERARCHY[roleKey] < ROLE_HIERARCHY[me.role]
  )).map(([v, r]) => ({ value: v, label: r.label }));

  // Security UI: restrict unit options for unit admins
  const baseUnitOptions = me.role === "superadmin"
    ? [{ value: "", label: "All Units (Super Admin)" }, ...units.map(u => ({ value: u.id, label: u.shortName }))]
    : units.filter(u => u.id === me.unitId).map(u => ({ value: u.id, label: u.shortName }));

  // A user whose stored unit matches no real unit would otherwise select nothing here:
  // the dropdown displays the first option while the form still holds the bad value, so
  // saving changes nothing and the edit looks broken. Show the bad value instead, so it
  // is obvious what is wrong and picking a real unit actually replaces it.
  const unitOptions = (uForm.unitId && !baseUnitOptions.some(o => o.value === uForm.unitId))
    ? [{ value: uForm.unitId, label: `⚠ Invalid unit (${uForm.unitId}) — pick one` }, ...baseUnitOptions]
    : baseUnitOptions;

  async function saveUser() {
    setSaving(true);
    try {
      if (userModal === "edit") { await apiCall("PUT", `/users/${uForm.id}`, uForm); }
      else { await apiCall("POST", "/users", uForm); }
      await refreshUsers(); setUserModal(null);
    } catch (error) { alert(error.response?.data?.detail || "Failed to save user"); }
    finally { setSaving(false); }
  }

  async function delUser(id) {
    if (id === me.id) { alert("Cannot delete yourself."); return; }
    if (window.confirm("Delete user?")) {
      try { await apiCall("DELETE", `/users/${id}`); await refreshUsers(); }
      catch (error) { alert("Failed to delete user"); }
    }
  }

  async function handleResetPassword() {
    if (!resetConfirm) return;
    setResetting(true);
    try {
      const res = await apiCall("POST", `/users/${resetConfirm.id}/reset-password`);
      alert(`Password reset. New password: ${res.default_password} \u2014 please inform the user.`);
      setResetConfirm(null);
    } catch (error) { alert(error.response?.data?.detail || "Failed to reset password"); }
    finally { setResetting(false); }
  }

  async function saveUnit() {
    setSaving(true);
    try {
      if (unitModal === "edit") {
        await apiCall("PUT", `/units/${unForm.id}`, unForm);
      } else {
        await apiCall("POST", "/units", unForm);
      }
      await refreshUnits();
      setUnitModal(null);
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save unit");
    } finally { setSaving(false); }
  }

  async function confirmDeleteUnit() {
    if (!deleteUnit) return;
    setDeletingUnit(true); setDeleteUnitErr("");
    try {
      await apiCall("DELETE", `/units/${deleteUnit.id}`);
      // If the deleted unit was the one on screen, App switches away once the
      // refreshed list no longer contains it.
      await refreshUnits();
      setDeleteUnit(null);
    } catch (error) {
      setDeleteUnitErr(error.response?.data?.detail || "Failed to delete unit");
    } finally { setDeletingUnit(false); }
  }

  // Twelve sections across one row left the long names wrapping onto two lines and
  // squeezed the rest off a laptop screen. They read down a side list instead, where
  // the name has room and the thirteenth costs nothing.
  const sections = [
    { id: "users", label: "Users" },
    { id: "units", label: "Units" },
    { id: "departments", label: "Departments" },
    { id: "floors", label: "Floors" },
    ...(me.role === "superadmin" ? [
      { id: "pmchecksheets", label: "PM Check Sheets" },
      { id: "mtypes", label: "Maintenance Types" },
      { id: "rbac", label: "RBAC" },
      { id: "apikeys", label: "API Keys" },
      { id: "backup", label: "Backup & Restore" },
      { id: "hrexport", label: "HR Export (Monthly JSON)" },
      { id: "hrkpi", label: "HR KPI Export" },
    ] : []),
    ...((me.role === "superadmin" || me.role === "unit_admin") ? [{ id: "audit", label: "Audit Log" }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="settings">
      <div><h2 className="font-bold text-gray-800">Settings &amp; Administration</h2><p className="text-xs text-gray-500">Manage users, roles, and unit configuration</p></div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Sticky so the section list stays reachable part-way down a long Audit Log,
            and a scrollable strip on a narrow screen rather than a column eating the
            width the tables need. */}
        <nav className="shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white lg:sticky lg:top-4 lg:w-56" data-testid="settings-nav">
          <div className="flex gap-1 overflow-x-auto p-1.5 lg:block lg:space-y-0.5 lg:overflow-visible">
            {sections.map(sec => (
              <button key={sec.id} onClick={() => setTab(sec.id)}
                className={`w-full shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors lg:whitespace-normal ${
                  tab === sec.id ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"}`}
                data-testid={`tab-${sec.id}`}>
                {sec.label}
              </button>
            ))}
          </div>
        </nav>

        <div className="min-w-0 flex-1 space-y-4">

      {tab === "users" && (
        <>
          {userModal && (
            <Modal title={userModal === "edit" ? "Edit User" : "Add User"} onClose={() => setUserModal(null)}>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Full Name" value={uForm.name || ""} onChange={e => setUForm(f => ({ ...f, name: e.target.value }))} />
                <Input label="Username" value={uForm.username || ""} onChange={e => setUForm(f => ({ ...f, username: e.target.value }))} />
                {/* The box itself is for SETTING a password; it starts empty and leaving
                    it blank keeps the current one, because the API drops an empty value
                    rather than saving it. The line underneath shows the password that
                    currently opens the account — Super Admin only, and only once the
                    eye is clicked. */}
                <div>
                  <Input label="Password" value={uForm.password || ""} onChange={e => setUForm(f => ({ ...f, password: e.target.value }))} placeholder={userModal === "edit" ? "Leave blank to keep current" : ""} />
                  {userModal === "edit" && me.role === "superadmin" && (
                    <div className="mt-1 flex items-center gap-1.5 text-xs" data-testid="current-password-row">
                      <span className="text-gray-500 shrink-0">Current:</span>
                      {uForm.plainPassword ? (
                        <>
                          <code className="min-w-0 flex-1 truncate rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-800" data-testid="current-password-value">
                            {showPwd ? uForm.plainPassword : "••••••••"}
                          </code>
                          <button type="button" onClick={() => setShowPwd(v => !v)}
                            className="shrink-0 rounded p-0.5 text-gray-400 transition hover:text-gray-700"
                            title={showPwd ? "Hide password" : "Show password"}
                            aria-label={showPwd ? "Hide password" : "Show password"}
                            data-testid="toggle-current-password">
                            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </>
                      ) : (
                        // Every password set before this was kept is a bcrypt hash and
                        // cannot be read back. Saying so beats an empty box that looks
                        // like a bug.
                        <span className="text-gray-400">not recorded — set a new one here to record it</span>
                      )}
                    </div>
                  )}
                </div>
                <Input label="Email" value={uForm.email || ""} onChange={e => setUForm(f => ({ ...f, email: e.target.value }))} />
                <Select label="Role" value={uForm.role || "viewer"} onChange={e => setUForm(f => ({ ...f, role: e.target.value }))} options={allowedRoles} />
                {/* Send "" for All Units, never null. The backend drops null fields as
                    "not supplied", so turning the empty choice into null meant picking
                    All Units silently changed nothing — which is how a user with a bad
                    unit stayed broken however many times it was saved. */}
                <Select label="Unit" value={uForm.unitId || ""} onChange={e => setUForm(f => ({ ...f, unitId: e.target.value }))} options={unitOptions} />
              </div>
              <div className="flex gap-2 mt-4"><Btn onClick={saveUser} disabled={saving} testId="save-user-btn">{saving ? "Saving..." : "Save"}</Btn><Btn onClick={() => setUserModal(null)} variant="ghost">Cancel</Btn></div>
            </Modal>
          )}
          {resetConfirm && (
            <Modal title="Reset Password" onClose={() => setResetConfirm(null)}>
              <p className="text-sm text-gray-700 mb-4">Reset password for <strong>{resetConfirm.name}</strong> to the default (<code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Aampl@123</code>)?</p>
              <div className="flex gap-2">
                <Btn onClick={handleResetPassword} disabled={resetting} testId="confirm-reset-btn">{resetting ? "Resetting..." : "Confirm Reset"}</Btn>
                <Btn onClick={() => setResetConfirm(null)} variant="ghost">Cancel</Btn>
              </div>
            </Modal>
          )}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">
              {filteredUsers.length} {filteredUsers.length === 1 ? "user" : "users"}
              {unitFilter !== "__all__" && <span className="text-gray-400"> of {visibleUsers.length}</span>}
            </span>
            <Btn onClick={() => { setUForm({ id: null, name: "", username: "", password: "", role: "viewer", unitId: me.unitId || "u1", email: "" }); setShowPwd(false); setUserModal("add"); }} testId="add-user-btn">+ Add User</Btn>
          </div>
          <div className="space-y-3">
            {/* The section list already occupies the left edge; a second column of
                navigation beside it would leave the table almost no room. The unit
                filter reads as a row of chips above the table instead. */}
            <div className="flex flex-wrap items-center gap-1.5" data-testid="user-unit-filter">
              {userGroups.map(g => (
                <button key={g.key} type="button" onClick={() => setUnitFilter(g.key)}
                  title={g.hint || undefined}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                    unitFilter === g.key
                      ? "border-blue-600 bg-blue-600 text-white"
                      : `border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 ${g.invalid ? "text-amber-600" : "text-gray-600"}`}`}
                  data-testid={`user-unit-filter-${g.key}`}>
                  <span className="font-medium">{g.label}</span>
                  <span className={`rounded-full px-1.5 text-[10px] font-bold tabular-nums ${
                    unitFilter === g.key ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"}`}>{g.count}</span>
                </button>
              ))}
            </div>
            <div className="min-w-0 overflow-x-auto rounded-xl border bg-white">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b">{["Name", "Username", "Role", "Unit", "Email", "Actions"].map(h => <th key={h} className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{u.name}{u.id === me.id && <span className="ml-2 text-xs text-blue-500">(you)</span>}</td>
                    <td className="px-4 py-3 font-mono text-gray-600 text-xs">{u.username}</td>
                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                    {/* A stored unit that matches nothing rendered as an empty cell, which
                        reads as "no unit set" rather than "this value is wrong". */}
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {!u.unitId
                        ? "All Units"
                        : (units.find(un => un.id === u.unitId)?.shortName
                           || <span className="text-amber-600 font-medium" title={`Stored value: ${u.unitId}`}>⚠ Invalid unit</span>)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setUForm({ ...u }); setShowPwd(false); setUserModal("edit"); }} className="text-blue-400 hover:text-blue-600 text-xs" data-testid={`edit-user-${u.id}`}>Edit</button>
                        {me.role === "superadmin" && u.id !== me.id && <button onClick={() => setResetConfirm(u)} className="text-amber-500 hover:text-amber-700 text-xs" data-testid={`reset-pwd-${u.id}`}>Reset Pwd</button>}
                        {canSeeAll && u.id !== me.id && <button onClick={() => delUser(u.id)} className="text-red-300 hover:text-red-500 text-xs" data-testid={`delete-user-${u.id}`}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <p className="px-4 py-6 text-center text-xs text-gray-400">No users in this unit.</p>
            )}
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <h3 className="font-semibold text-gray-700 text-sm">Role Permissions</h3>
                {/* This was a fixed table of ticks describing the roles as first seeded.
                    It kept saying so after the roles had been edited in RBAC, which is
                    exactly the thing an admin checks when a permission appears not to
                    work. It now reads the live roles. */}
                <p className="text-xs text-gray-500">
                  Live from the saved roles{rolesErr ? "" : rolesLoading ? " — loading…" : ""}.
                  {permDraft && " Tick or untick, then Save."}
                </p>
                {permErr && <p className="mt-1 text-xs text-red-600" data-testid="perm-save-error">{permErr}</p>}
              </div>
              <div className="flex items-center gap-2">
                {!!roles.length && !permDraft && (
                  <span className="text-xs text-gray-400">{permissionRows.length} permissions &middot; {roles.length} roles</span>
                )}
                {can(me, "manageRoles") && !!roles.length && (permDraft ? (
                  <>
                    <button onClick={savePerms} disabled={permSaving}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                      data-testid="save-perms-btn">{permSaving ? "Saving…" : "Save"}</button>
                    <button onClick={() => { setPermDraft(null); setPermErr(""); }} disabled={permSaving}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
                      data-testid="cancel-perms-btn">Cancel</button>
                  </>
                ) : (
                  <button onClick={startPermEdit}
                    className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                    data-testid="edit-perms-btn">Edit</button>
                ))}
              </div>
            </div>
            {rolesErr ? (
              <div className="text-xs text-gray-500">Could not load roles: {rolesErr}</div>
            ) : !roles.length ? (
              <div className="text-xs text-gray-500">{rolesLoading ? "Loading…" : "No roles found."}</div>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg bg-white">
                <table className="w-full text-xs" data-testid="role-permissions-matrix">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="border-b">
                      <th className="text-left px-3 py-1.5 text-gray-500 font-semibold">Permission</th>
                      {roles.map(r => (
                        <th key={r.name} className="px-3 py-1.5 text-gray-500 font-semibold text-center whitespace-nowrap">
                          {r.displayName || r.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {permissionRows.map((row, idx) => {
                      const newGroup = idx === 0 || permissionRows[idx - 1].group !== row.group;
                      return (
                        <tr key={row.key} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-1.5 text-gray-700">
                            {newGroup && <span className="block text-[10px] uppercase tracking-wide text-gray-400 mt-1">{row.group}</span>}
                            <span className="font-medium">{row.label}</span>
                          </td>
                          {roles.map(r => {
                            const locked = r.name === "superadmin";
                            const has = permDraft
                              ? (permDraft[r.name] || []).includes(row.key)
                              : (r.permissions || []).includes(row.key);
                            return (
                              <td key={r.name} className="px-3 py-1.5 text-center"
                                data-testid={`perm-${row.key}-${r.name}`}>
                                {permDraft && !locked ? (
                                  <input type="checkbox" checked={has} disabled={permSaving}
                                    onChange={() => togglePerm(r.name, row.key)}
                                    className="h-3.5 w-3.5 cursor-pointer accent-blue-600 align-middle"
                                    aria-label={`${row.label} for ${r.displayName || r.name}`} />
                                ) : (
                                  <span className={`font-bold ${has ? "text-green-600" : "text-gray-300"}`}
                                    title={permDraft && locked ? "Super Admin always has every permission" : undefined}>
                                    {has ? "\u2713" : "\u2013"}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === "units" && (
        <>
          {unitModal && (
            <Modal title={unitModal === "edit" ? "Edit Unit Settings" : "Add New Unit"} onClose={() => setUnitModal(null)}>
              <div className="grid grid-cols-1 gap-3">
                <Input label="Unit Name *" value={unForm.name || ""} onChange={e => setUnForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Unit-IV \u2014 New Plant" />
                <Input label="Short Name *" value={unForm.shortName || ""} onChange={e => setUnForm(f => ({ ...f, shortName: e.target.value }))} placeholder="e.g. Unit-IV" />
                <Input label="Address" value={unForm.address || ""} onChange={e => setUnForm(f => ({ ...f, address: e.target.value }))} />
                <Select label="Color Theme" value={unForm.color || "blue"} onChange={e => setUnForm(f => ({ ...f, color: e.target.value }))} options={UNIT_COLORS} />
                <Input label="Last Updated (e.g. Apr-2026)" value={unForm.lastUpdated || ""} onChange={e => setUnForm(f => ({ ...f, lastUpdated: e.target.value }))} />
              </div>
              <div className="mt-3 border-t pt-3">
                <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Shift Timing (used for MTBF / MTTR)</p>
                {/* These used to show "08:30" and "18:00" whether or not anything was
                    stored, so an empty unit looked configured and MTBF appeared to be
                    measured on hours nobody had set. They now show what is actually
                    saved, and say plainly when nothing is. */}
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-gray-500">Shift Start</label><TimeField value={unForm.shiftStart || ""} onChange={v => setUnForm(f => ({ ...f, shiftStart: v }))} testId="unit-shift-start" /></div>
                  <div><label className="text-xs text-gray-500">Shift End</label><TimeField value={unForm.shiftEnd || ""} onChange={v => setUnForm(f => ({ ...f, shiftEnd: v }))} testId="unit-shift-end" /></div>
                  <div className="col-span-2"><label className="text-xs text-gray-500">Daily Break / Lunch (minutes)</label><input type="number" min="0" step="5" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" value={unForm.breakMinutes ?? 0} onChange={e => setUnForm(f => ({ ...f, breakMinutes: e.target.value === "" ? 0 : Number(e.target.value) }))} data-testid="unit-break-min" /></div>
                </div>
                <p className="mt-2 text-xs" data-testid="unit-shift-summary">{shiftSummary(unForm)}</p>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Lunch Break (subtracted from breakdown downtime)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-500">Lunch Start</label><TimeField value={unForm.lunchStart || "13:00"} onChange={v => setUnForm(f => ({ ...f, lunchStart: v }))} testId="unit-lunch-start" /></div>
                    <div><label className="text-xs text-gray-500">Lunch End</label><TimeField value={unForm.lunchEnd || "13:30"} onChange={v => setUnForm(f => ({ ...f, lunchEnd: v }))} testId="unit-lunch-end" /></div>
                  </div>
                </div>
                <div className="mt-2">
                  <label className="text-xs text-gray-500">Working Days</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => {
                      const days = Array.isArray(unForm.workingDays) ? unForm.workingDays : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                      const on = days.includes(d);
                      return (
                        <button key={d} type="button" data-testid={`unit-day-${d}`}
                          onClick={() => setUnForm(f => { const cur = Array.isArray(f.workingDays) ? f.workingDays : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; const next = cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d]; return { ...f, workingDays: next }; })}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${on ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-white text-gray-500 border-gray-200"}`}>
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {unitModal !== "edit" && (
                <p className="text-xs text-gray-500 mt-3 bg-blue-50 border border-blue-200 rounded p-2">11 standard departments will be auto-created for this unit (Press Shop, Machine Shop, Tool Room, Assy Shop, Wire Cut, Utility, Standard Room, Raw Material Area, Factory Area, Office Area, Final Inspection).</p>
              )}
              <div className="flex gap-2 mt-4 items-center">
                <Btn onClick={saveUnit} disabled={saving} testId="save-unit-btn">{saving ? "Saving..." : (unitModal === "edit" ? "Save" : "Create Unit")}</Btn>
                <Btn onClick={() => setUnitModal(null)} variant="ghost">Cancel</Btn>
                {/* Kept away from Save — deleting a unit is not something to hit by accident.
                    Closes this form and asks for confirmation before anything happens. */}
                {unitModal === "edit" && me.role === "superadmin" && units.length > 1 && (
                  <div className="ml-auto">
                    <Btn onClick={() => { setUnitModal(null); setDeleteUnitErr(""); setDeleteUnit(unForm); }}
                      variant="danger" testId="delete-unit-btn">Delete Unit</Btn>
                  </div>
                )}
              </div>
            </Modal>
          )}
          {deleteUnit && (
            <Modal title="Delete Unit" onClose={() => { if (!deletingUnit) { setDeleteUnit(null); setDeleteUnitErr(""); } }}>
              <p className="text-sm text-gray-700 mb-2">
                Delete <strong>{deleteUnit.name}</strong> ({deleteUnit.shortName})?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4">
                Only an empty unit can be deleted. If it still holds machines, PM plans, maintenance
                log entries, spare parts or users, the delete is refused and nothing is touched.
                The departments created automatically with the unit are removed along with it.
              </div>
              {deleteUnitErr && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-3" data-testid="delete-unit-error">
                  {deleteUnitErr}
                </div>
              )}
              <div className="flex gap-2">
                <Btn onClick={confirmDeleteUnit} disabled={deletingUnit} variant="danger" testId="confirm-delete-unit-btn">
                  {deletingUnit ? "Deleting..." : "Delete Unit"}
                </Btn>
                <Btn onClick={() => { setDeleteUnit(null); setDeleteUnitErr(""); }} variant="ghost" disabled={deletingUnit}>Cancel</Btn>
              </div>
            </Modal>
          )}
          {me.role === "superadmin" && (
            <div className="flex justify-end mb-3">
              <Btn onClick={() => { setUnForm({ name: "", shortName: "", address: "", color: "blue", lastUpdated: "", shiftStart: "08:30", shiftEnd: "18:00", workingDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], breakMinutes: 0, lunchStart: "13:00", lunchEnd: "13:30" }); setUnitModal("add"); }} testId="add-unit-btn">+ Add Unit</Btn>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-4">
            {units.map(u => {
              return (
                <div key={u.id} className={`rounded-xl border p-4 ${UC2[u.color] || UC2.blue}`}>
                  <div className="font-bold text-base mb-1">{u.name}</div>
                  {/* Two units can carry the same name \u2014 the short name is what tells them
                      apart in the switcher, so it is shown here too. */}
                  <div className="text-xs font-semibold opacity-80 mb-1">{u.shortName || "\u2014"}</div>
                  <div className="text-xs opacity-70 mb-2">{u.address || <span className="italic opacity-60">No address</span>}</div>
                  <div className="text-xs mb-3">Last updated: <strong>{u.lastUpdated || "\u2014"}</strong></div>
                  {can(me, "accessSettings") && <Btn onClick={() => { setUnForm({ ...u }); setUnitModal("edit"); }} size="sm" variant="ghost" testId={`edit-unit-${u.id}`}>Edit Unit Info</Btn>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "departments" && (
        <div className="space-y-4" data-testid="manage-departments">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{departments.length} departments for current unit</span>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Input label="New Department Name" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} placeholder="e.g. WELDING SHOP" /></div>
            <Btn onClick={async () => {
              if (!newDeptName.trim()) return;
              setDeptSaving(true);
              try { await apiCall("POST", `/departments/${unitId}`, { name: newDeptName.trim().toUpperCase() }); await refreshDepartments(); setNewDeptName(""); }
              catch (error) { alert("Failed to add department"); }
              finally { setDeptSaving(false); }
            }} disabled={deptSaving || !newDeptName.trim()} testId="add-dept-btn">{deptSaving ? "Adding..." : "+ Add Department"}</Btn>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b"><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold w-12">#</th><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">Department Name</th><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold w-20">Actions</th></tr></thead>
              <tbody>
                {departments.map((d, i) => (
                  <tr key={d.id} className="border-b hover:bg-gray-50" data-testid={`dept-row-${d.id}`}>
                    <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{d.name}</td>
                    <td className="px-4 py-3">
                      <button onClick={async () => {
                        if (window.confirm(`Delete department "${d.name}"?`)) {
                          try { await apiCall("DELETE", `/departments/${unitId}/${d.id}`); await refreshDepartments(); }
                          catch (error) { alert("Failed to delete department"); }
                        }
                      }} className="text-red-400 hover:text-red-600 text-xs" data-testid={`delete-dept-${d.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {tab === "floors" && (
        <div className="space-y-4" data-testid="manage-floors">
          <div>
            <span className="text-sm text-gray-500">{floors.length} floors for current unit</span>
            <p className="text-xs text-gray-400 mt-0.5">
              Used by the Floor dropdown when adding or editing a machine, and by the PM Plan floor filter.
              Listed bottom to top; new floors are added at the end.
            </p>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Input label="New Floor Name" value={newFloorName} onChange={e => setNewFloorName(e.target.value)} placeholder="e.g. BASEMENT" /></div>
            <Btn onClick={async () => {
              if (!newFloorName.trim()) return;
              setFloorSaving(true); setFloorErr("");
              try { await apiCall("POST", `/floors/${unitId}`, { name: newFloorName.trim().toUpperCase() }); await refreshFloors(); setNewFloorName(""); }
              catch (error) { setFloorErr(error.response?.data?.detail || "Failed to add floor"); }
              finally { setFloorSaving(false); }
            }} disabled={floorSaving || !newFloorName.trim()} testId="add-floor-btn">{floorSaving ? "Adding..." : "+ Add Floor"}</Btn>
          </div>
          {floorErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="floor-error">{floorErr}</div>}
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b"><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold w-12">#</th><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">Floor Name</th><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold w-24">Machines</th><th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold w-20">Actions</th></tr></thead>
              <tbody>
                {floors.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400 text-xs">No floors configured for this unit.</td></tr>
                ) : floors.map((fl, i) => {
                  const inUse = (machines || []).filter(m => m.floor === fl.name).length;
                  const editing = editFloorId === fl.id;
                  const saveName = async () => {
                    const name = editFloorName.trim().toUpperCase();
                    if (!name || name === fl.name) { setEditFloorId(null); return; }
                    setFloorErr("");
                    try { await apiCall("PUT", `/floors/${unitId}/${fl.id}`, { name }); await refreshFloors(); await refreshMachines?.(); await refreshPM?.(); setEditFloorId(null); }
                    catch (error) { setFloorErr(error.response?.data?.detail || "Failed to rename floor"); }
                  };
                  const move = async direction => {
                    setFloorErr("");
                    try { await apiCall("PUT", `/floors/${unitId}/${fl.id}/move`, { direction }); await refreshFloors(); }
                    catch (error) { setFloorErr(error.response?.data?.detail || "Failed to move floor"); }
                  };
                  return (
                    <tr key={fl.id} className="border-b hover:bg-gray-50" data-testid={`floor-row-${fl.id}`}>
                      <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {editing ? (
                          <input
                            className="w-full border border-blue-300 rounded px-2 py-1 text-sm"
                            value={editFloorName}
                            autoFocus
                            onChange={e => setEditFloorName(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditFloorId(null); }}
                            data-testid={`edit-floor-input-${fl.id}`}
                          />
                        ) : fl.name}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{inUse || "\u2014"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-xs">
                          {editing ? (
                            <>
                              <button onClick={saveName} className="text-green-600 hover:text-green-800 font-semibold" data-testid={`save-floor-${fl.id}`}>Save</button>
                              <button onClick={() => setEditFloorId(null)} className="text-gray-400 hover:text-gray-600">Cancel</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => move("up")} disabled={i === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move up" data-testid={`floor-up-${fl.id}`}>{"\u2191"}</button>
                              <button onClick={() => move("down")} disabled={i === floors.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move down" data-testid={`floor-down-${fl.id}`}>{"\u2193"}</button>
                              <button onClick={() => { setEditFloorId(fl.id); setEditFloorName(fl.name); setFloorErr(""); }} className="text-blue-500 hover:text-blue-700" data-testid={`edit-floor-${fl.id}`}>Edit</button>
                              <button onClick={async () => {
                                setFloorErr("");
                                if (!window.confirm(`Delete floor "${fl.name}"?`)) return;
                                try { await apiCall("DELETE", `/floors/${unitId}/${fl.id}`); await refreshFloors(); }
                                catch (error) { setFloorErr(error.response?.data?.detail || "Failed to delete floor"); }
                              }} className="text-red-400 hover:text-red-600" data-testid={`delete-floor-${fl.id}`}>Delete</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "pmchecksheets" && me.role === "superadmin" && <PMCheckSheetsAdmin machines={machines} />}
      {tab === "mtypes" && me.role === "superadmin" && <MaintenanceTypesAdmin />}
      {tab === "rbac" && me.role === "superadmin" && <RBACAdmin />}
      {tab === "apikeys" && me.role === "superadmin" && <APIKeysAdmin />}
      {tab === "backup" && me.role === "superadmin" && <BackupRestore />}
      {tab === "hrexport" && me.role === "superadmin" && <HRMonthlyExport />}
      {tab === "hrkpi" && me.role === "superadmin" && <HRKpiExport />}
      {tab === "audit" && (me.role === "superadmin" || me.role === "unit_admin") && <AuditLog />}
        </div>
      </div>
    </div>
  );
}
